import { create } from 'zustand';
import { GetCredsystemCardInfoUseCase } from '~/credsystem/useCases/CredsystemUseCases/GetCredsystemCardInfoUseCase';
import {
  getCredsystemBarCode,
  getCredsystemCardInvoiceData,
  getCredsystemCardInvoiceDetails,
} from '~/credsystem/useCases';
import type { InvoiceDetails } from '../domain/invoices';

const INITIAL_STATE = {
  credsystemCardInfo: null,
  loading: false,
};

type credsystemDataState = {
  credsystemCardInfo: CredsystemCardInfoType | null;
  loading: boolean;
  getCardInfo: () => Promise<void>;
  getBarCode: (statementNumber?: string) => Promise<string | null>;
  getInvoices: () => Promise<void>;
  getInvoiceDetails: (invoiceId: number) => Promise<InvoiceDetails[] | null>;
  getCurrentInvoice: () => StatementType | null;
  clear: () => void;
};

const usecredsystemDataStore = create<credsystemDataState>((set, get) => ({
  credsystemCardInfo: null,
  loading: false,
  getCardInfo: async () => {
    set({ loading: true });
    try {
      const cardInfo = await GetCredsystemCardInfoUseCase();

      const credsystemCardInfo = get().credsystemCardInfo;

      if (cardInfo)
        set({
          credsystemCardInfo: {
            statements: credsystemCardInfo?.statements ?? null,
            birthDate: cardInfo.nascimento,
            canBuy: cardInfo.isHabilitadoCompra,
            cardNumber: cardInfo.numeroCartao,
            cardSituation: cardInfo.situacaoCartao,
            holder: cardInfo.titular,
            limit: {
              global: {
                available: cardInfo.Limite.Global.Disponivel,
                stipulated: cardInfo.Limite.Global.Estipulado,
              },
            },
            dueDate: cardInfo.vencimento || 'vencimento não encontrado',
          },
        });
    } catch (error) {
      console.log('useCredsystemData: getCardInfo, ', error);
    } finally {
      set({ loading: false });
    }
  },
  getBarCode: async (statementNumber?: string) => {
    try {
      if (!statementNumber) throw new Error('statementNumber is undefined');

      const barCode = await getCredsystemBarCode(statementNumber);

      return barCode;
    } catch (error) {
      console.log('useCredsystemData: getBarCode, ', error);
    }

    return null;
  },
  getInvoices: async () => {
    try {
      const invoices = await getCredsystemCardInvoiceData();

      if (invoices) {
        set((currentState) => ({
          credsystemCardInfo: {
            ...currentState.credsystemCardInfo,
            statements: invoices,
          },
        }));
      }
    } catch (error) {
      console.log('useCredsystemData: getStatementsData, ', error);
    }
  },
  getInvoiceDetails: async (invoiceId) => {
    try {
      const invoiceDetails = await getCredsystemCardInvoiceDetails(invoiceId);

      if (invoiceDetails) {
        return invoiceDetails;
      }
    } catch (error) {
      console.log('useCredsystemData: getStatementDetails, ', error);
    }
    return null;
  },
  getCurrentInvoice: () => {
    const invoices = get().credsystemCardInfo?.statements;
    if (invoices) {
      const currentInvoice = invoices.filter(
        (invoice) => invoice.situation === 'ATUAL'
      );
      if (currentInvoice?.[0]) return currentInvoice?.[0];
      return invoices.find((invoice) => invoice.situation === 'FUTURA') || null;
    }

    return null;
  },
  clear: () => {
    set(INITIAL_STATE);
  },
}));

export default usecredsystemDataStore;
